// src/pages/MiEquipo.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { miEquipoAPI, asignacionesAPI } from '../services/api';
import { Building2, Briefcase, Monitor, Smartphone, HardDrive, Loader2, AlertCircle, Network, UserCircle, Star, Car, Tag, CreditCard, Package, Save, Trash2 } from 'lucide-react';
import { Users, ChevronDown, ChevronRight, User, FolderTree, Plus, X, Key, DollarSign, Search, RotateCcw } from '../components/icons';

// Componente Modal para agregar activo operacional
function AddAssetModal({ isOpen, onClose, onAdd, usuario, isSubmitting }) {
    const [formData, setFormData] = useState({
        nombre: '',
        categoria: 'Vehículo',
        codigo: '',
        marca: '',
        modelo: '',
    });

    if (!isOpen) return null;

    const categories = ['Vehículo', 'TAG', 'Caja Chica', 'Llave', 'Acceso Físico', 'Credencial', 'EPP', 'Otro'];

    // Determinar qué campos mostrar según la categoría
    const showMarcaModelo = ['Vehículo', 'EPP'].includes(formData.categoria);
    const showCodigo = formData.categoria !== 'Otro';

    const handleSubmit = (e) => {
        e.preventDefault();

        // Si el código está oculto o vacío, generamos uno interno
        const finalData = { ...formData };
        if (!showCodigo || !finalData.codigo) {
            finalData.codigo = `OP-${formData.categoria.substring(0, 3).toUpperCase()}-${Date.now().toString().slice(-6)}`;
        }

        onAdd(finalData);
    };

    // Obtener etiquetas y placeholders dinámicos
    const getCodeLabel = () => {
        if (formData.categoria === 'Vehículo') return 'Patente';
        if (formData.categoria === 'Caja Chica') return 'Identificador';
        return 'Código / Serial';
    };

    const getCodePlaceholder = () => {
        if (formData.categoria === 'Vehículo') return 'Ej: ABCD-12 (Opcional)';
        if (formData.categoria === 'Caja Chica') return 'Ej: CC-01 (Opcional)';
        return 'Ej: 123456 (Opcional)';
    };

    const getNamePlaceholder = () => {
        if (formData.categoria === 'Vehículo') return 'Ej: Camioneta Suzuki';
        if (formData.categoria === 'Caja Chica') return 'Ej: Caja Chica Administración';
        if (formData.categoria === 'TAG') return 'Ej: TAG Autopista Central';
        return 'Ej: Descripción o Nombre';
    };

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 dark:border-gray-700 animate-in zoom-in-95 duration-200">
                <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg">
                            <Plus className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">Asignar Activo Operacional</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Para {usuario.nombre} {usuario.apellido}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                        <X className="w-5 h-5 text-gray-400" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1.5 ml-1">Nombre del Activo</label>
                            <input
                                required
                                autoFocus
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none transition-all text-sm"
                                placeholder={getNamePlaceholder()}
                                value={formData.nombre}
                                onChange={e => setFormData({ ...formData, nombre: e.target.value })}
                            />
                        </div>

                        <div className={showCodigo ? "col-span-1" : "col-span-2"}>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1.5 ml-1">Categoría</label>
                            <select
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none transition-all text-sm appearance-none cursor-pointer"
                                value={formData.categoria}
                                onChange={e => setFormData({ ...formData, categoria: e.target.value })}
                            >
                                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>

                        {showCodigo && (
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1.5 ml-1">{getCodeLabel()}</label>
                                <input
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none transition-all text-sm"
                                    placeholder={getCodePlaceholder()}
                                    value={formData.codigo}
                                    onChange={e => setFormData({ ...formData, codigo: e.target.value })}
                                />
                            </div>
                        )}

                        {showMarcaModelo && (
                            <>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1.5 ml-1">Marca (Opcional)</label>
                                    <input
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none transition-all text-sm"
                                        placeholder="Ej: Suzuki"
                                        value={formData.marca}
                                        onChange={e => setFormData({ ...formData, marca: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1.5 ml-1">Modelo (Opcional)</label>
                                    <input
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none transition-all text-sm"
                                        placeholder="Ej: Grand Vitara"
                                        value={formData.modelo}
                                        onChange={e => setFormData({ ...formData, modelo: e.target.value })}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div className="pt-4 flex items-center gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white text-sm font-semibold shadow-lg shadow-emerald-600/20 transition-all hover:scale-[1.02] active:scale-95"
                        >
                            {isSubmitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            Guardar Activo
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
}

// Componente para un nodo del árbol de áreas
function AreaNode({
    area,
    level = 0,
    defaultExpanded = true,
    onAssetAdded,
    selectedPerson,
    setSelectedPerson,
    isAddModalOpen,
    setIsAddModalOpen,
    setSubmittingAsset,
    submittingAsset
}) {
    const [expanded, setExpanded] = useState(defaultExpanded && level < 2);
    const { showSuccess, showError } = useToast();

    const hasChildren = area.children && area.children.length > 0;
    const hasCargos = area.cargos && area.cargos.length > 0;
    const totalMiembros = area._count?.cargos || area.cargos?.length || 0;
    const totalSubAreas = area._count?.subAreas || area.children?.length || 0;

    // Colores según tipo de área
    const areaColors = {
        'Directorio': { bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-300 dark:border-purple-700', badge: 'bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300', icon: 'text-purple-500' },
        'Gerencia': { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-300 dark:border-blue-700', badge: 'bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300', icon: 'text-blue-500' },
        'Subgerencia': { bg: 'bg-cyan-50 dark:bg-cyan-900/20', border: 'border-cyan-300 dark:border-cyan-700', badge: 'bg-cyan-100 dark:bg-cyan-800 text-cyan-700 dark:text-cyan-300', icon: 'text-cyan-500' },
        'Departamento': { bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-300 dark:border-emerald-700', badge: 'bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-300', icon: 'text-emerald-500' },
        'Equipo': { bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-300 dark:border-amber-700', badge: 'bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-300', icon: 'text-amber-500' },
    };

    const colors = areaColors[area.tipoArea] || areaColors['Departamento'];

    const getCategoriaIcon = (categoria, clase) => {
        const cat = categoria?.toLowerCase() || '';

        // Iconos por clase y categoría
        if (clase === 'Operacional') {
            if (cat.includes('vehiculo') || cat.includes('auto') || cat.includes('camion')) return <Car className="w-3.5 h-3.5" />;
            if (cat.includes('llave') || cat.includes('acceso')) return <Key className="w-3.5 h-3.5" />;
            if (cat.includes('tag')) return <Tag className="w-3.5 h-3.5" />;
            if (cat.includes('credencial')) return <CreditCard className="w-3.5 h-3.5" />;
            if (cat.includes('caja')) return <DollarSign className="w-3.5 h-3.5" />;
            return <Package className="w-3.5 h-3.5" />;
        }

        switch (cat) {
            case 'notebook':
            case 'desktop':
            case 'computador':
                return <Monitor className="w-3.5 h-3.5" />;
            case 'celular':
            case 'tablet':
                return <Smartphone className="w-3.5 h-3.5" />;
            default:
                return <HardDrive className="w-3.5 h-3.5" />;
        }
    };

    return (
        <div className={`${level > 0 ? 'ml-6' : ''}`}>
            {/* Conector visual de línea vertical */}
            {level > 0 && (
                <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-200 dark:bg-gray-700" />
            )}

            {/* Cabecera del área */}
            <div
                className={`group relative flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 
          ${colors.bg} ${colors.border} border
          hover:shadow-md hover:scale-[1.01]`}
                onClick={() => setExpanded(!expanded)}
            >
                {/* Icono expand/collapse */}
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                    {(hasChildren || hasCargos) ? (
                        expanded ?
                            <ChevronDown className={`w-5 h-5 ${colors.icon} transition-transform duration-200`} /> :
                            <ChevronRight className={`w-5 h-5 ${colors.icon} transition-transform duration-200`} />
                    ) : (
                        <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600" />
                    )}
                </div>

                {/* Icono del tipo de área */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-lg ${colors.badge} flex items-center justify-center`}>
                    <Building2 className="w-4 h-4" />
                </div>

                {/* Nombre y tipo */}
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                        {area.nombre}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{area.tipoArea}</p>
                </div>

                {/* Badges de conteo */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    {totalMiembros > 0 && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors.badge}`}>
                            <Users className="w-3 h-3" />
                            {totalMiembros}
                        </span>
                    )}
                    {totalSubAreas > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                            <FolderTree className="w-3 h-3" />
                            {totalSubAreas}
                        </span>
                    )}
                </div>
            </div>

            {/* Contenido expandido */}
            {expanded && (
                <div className="mt-2 space-y-2 relative">
                    {/* Lista de miembros */}
                    {hasCargos && Array.isArray(area.cargos) && (
                        <div className="ml-4 space-y-1.5">
                            {area.cargos.map((cargo) => (
                                <div key={cargo.id || `${cargo.usuario?.id}-${cargo.cargo}`}>
                                    {/* ... content omitted for brevity, keeping structure ... */}
                                    <div
                                        className={`flex items-center gap-3 p-2.5 rounded-lg transition-all duration-150 cursor-pointer
                      ${selectedPerson === cargo.usuario?.id
                                                ? 'bg-white dark:bg-gray-700 shadow-sm ring-1 ring-gray-200 dark:ring-gray-600'
                                                : 'hover:bg-white/60 dark:hover:bg-gray-700/40'
                                            }`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedPerson(selectedPerson === cargo.usuario?.id ? null : cargo.usuario?.id);
                                        }}
                                    >
                                        {/* Avatar */}
                                        <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center
                      ${cargo.esJefatura
                                                ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white'
                                                : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                                            }`}>
                                            {cargo.esJefatura ? (
                                                <Star className="w-4 h-4" />
                                            ) : (
                                                <UserCircle className="w-5 h-5" />
                                            )}
                                        </div>

                                        {/* Info persona */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                                                    {cargo.usuario?.nombre} {cargo.usuario?.apellido}
                                                </span>
                                                {cargo.esJefatura && (
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-300">
                                                        Jefe
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                {cargo.cargo} {cargo.usuario?.empresa ? `· ${cargo.usuario.empresa}` : ''}
                                            </p>
                                        </div>

                                        {/* Indicador de activos */}
                                        <div className="flex-shrink-0 flex items-center gap-2">
                                            {(cargo.usuario?.activosTI?.length > 0 || cargo.usuario?.asignaciones?.length > 0) && (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium">
                                                    {(cargo.usuario?.activosTI?.length || cargo.usuario?.asignaciones?.length)} TI
                                                </span>
                                            )}
                                            {cargo.usuario?.activosOperacionales?.length > 0 && (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 font-medium">
                                                    {cargo.usuario.activosOperacionales.length} OP
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Activos expandibles */}
                                    {selectedPerson === cargo.usuario?.id && (
                                        <div className="ml-12 mt-1 mb-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm animate-in slide-in-from-top-2 duration-200">
                                            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-50 dark:border-gray-700">
                                                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                                                    Activos Asignados
                                                </p>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsAddModalOpen(true);
                                                    }}
                                                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold transition-all border border-emerald-100 dark:border-emerald-800 shadow-sm hover:scale-105"
                                                >
                                                    <Plus className="w-3 h-3" />
                                                    AGREGAR OPERACIONAL
                                                </button>
                                            </div>

                                            {/* Sección TI */}
                                            {(cargo.usuario?.activosTI?.length > 0 || cargo.usuario?.asignaciones?.length > 0) ? (
                                                <div className="space-y-1.5 mb-4">
                                                    <p className="text-[9px] font-bold text-blue-400 dark:text-blue-500 uppercase mb-1">Informáticos</p>
                                                    {(cargo.usuario?.activosTI || cargo.usuario?.asignaciones || []).map((asig) => (
                                                        <div key={asig.activo?.id || asig.id} className="flex items-center gap-2 text-[11px] text-gray-700 dark:text-gray-300 bg-gray-50/50 dark:bg-gray-900/30 p-1.5 rounded-lg border border-transparent hover:border-gray-100 dark:hover:border-gray-700 transition-colors">
                                                            <span className="text-blue-400/80">
                                                                {getCategoriaIcon(asig.activo?.categoria, 'TI')}
                                                            </span>
                                                            <span className="font-bold text-gray-900 dark:text-white">{asig.activo?.codigo}</span>
                                                            <span className="text-gray-300 dark:text-gray-600">|</span>
                                                            <span className="truncate flex-1">{asig.activo?.nombre || `${asig.activo?.marca} ${asig.activo?.modelo}`}</span>
                                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase
                                                                ${asig.activo?.estado === 'Activo'
                                                                    ? 'text-green-600 dark:text-green-400'
                                                                    : 'text-gray-500'
                                                                }`}>
                                                                {asig.activo?.estado}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : null}

                                            {/* Sección Operacionales */}
                                            {cargo.usuario?.activosOperacionales?.length > 0 ? (
                                                <div className="space-y-1.5">
                                                    <p className="text-[9px] font-bold text-emerald-500 dark:text-emerald-400 uppercase mb-1">Operacionales</p>
                                                    {(cargo.usuario?.activosOperacionales || []).map((asig) => (
                                                        <div key={asig.activo?.id || asig.id} className="flex items-center gap-2 text-[11px] text-gray-700 dark:text-gray-300 bg-emerald-50/20 dark:bg-emerald-900/10 p-1.5 rounded-lg border border-emerald-100/50 dark:border-emerald-800/30 hover:border-emerald-200 transition-colors">
                                                            <span className="text-emerald-500/80">
                                                                {getCategoriaIcon(asig.activo?.categoria, 'Operacional')}
                                                            </span>
                                                            <span className="font-bold text-gray-900 dark:text-white">{asig.activo?.codigo}</span>
                                                            <span className="text-emerald-200 dark:text-emerald-800">|</span>
                                                            <span className="truncate flex-1">{asig.activo?.nombre || `${asig.activo?.marca} ${asig.activo?.modelo}`}</span>
                                                            <div className="flex items-center gap-2">
                                                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase text-emerald-600 dark:text-emerald-400`}>
                                                                    {asig.activo?.estado}
                                                                </span>
                                                                <button
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        if (window.confirm('¿Estás seguro de que deseas devolver este activo operacional?')) {
                                                                            try {
                                                                                await miEquipoAPI.devolver(asig.id, {
                                                                                    observaciones: 'Devuelto desde Mi Equipo (Vista Líder)'
                                                                                });
                                                                                showSuccess('Activo operacional devuelto correctamente');
                                                                                if (onAssetAdded) onAssetAdded();
                                                                            } catch (err) {
                                                                                showError('Error al devolver el activo');
                                                                            }
                                                                        }
                                                                    }}
                                                                    className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-500 rounded transition-colors flex items-center justify-center mr-1"
                                                                    title="Devolver Activo Operacional"
                                                                >
                                                                    <RotateCcw className="w-3.5 h-3.5" />
                                                                </button>
                                                                <button
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        if (window.confirm('¿Estás seguro de que deseas eliminar este activo operacional de forma permanente?')) {
                                                                            try {
                                                                                await miEquipoAPI.desasignarOperacional(asig.id);
                                                                                showSuccess('Activo operacional eliminado');
                                                                                if (onAssetAdded) onAssetAdded();
                                                                            } catch (err) {
                                                                                showError('Error al eliminar el activo');
                                                                            }
                                                                        }
                                                                    }}
                                                                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-500 rounded transition-colors"
                                                                    title="Eliminar Activo Operacional"
                                                                >
                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                !(cargo.usuario?.activosTI?.length > 0 || cargo.usuario?.asignaciones?.length > 0) && (
                                                    <p className="text-xs text-center py-4 text-gray-400 italic">No hay activos asignados</p>
                                                )
                                            )}
                                        </div>
                                    )}

                                </div>
                            ))}
                        </div>
                    )}

                    {/* Sub-áreas recursivas */}
                    {hasChildren && Array.isArray(area.children) && area.children.map((child) => (
                        <AreaNode
                            key={child.id}
                            area={child}
                            level={level + 1}
                            onAssetAdded={onAssetAdded}
                            selectedPerson={selectedPerson}
                            setSelectedPerson={setSelectedPerson}
                            isAddModalOpen={isAddModalOpen}
                            setIsAddModalOpen={setIsAddModalOpen}
                            setSubmittingAsset={setSubmittingAsset}
                            submittingAsset={submittingAsset}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default function MiEquipo() {
    const { user } = useAuth();
    const { showSuccess, showError } = useToast();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Estado centralizado para selección y modales
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [submittingAsset, setSubmittingAsset] = useState(false);

    // Función auxiliar para buscar un usuario en profundidad en el árbol
    const findUsuarioInTree = (areas, id) => {
        if (!areas) return null;
        for (const area of areas) {
            // Buscar en cargos de esta área
            const cargo = area.cargos?.find(c => c.usuario?.id === id);
            if (cargo) return cargo.usuario;
            // Buscar en sub-áreas
            if (area.children) {
                const found = findUsuarioInTree(area.children, id);
                if (found) return found;
            }
        }
        return null;
    };

    const targetAddingUser = isAddModalOpen ? findUsuarioInTree(data?.equipo, selectedPerson) : null;

    const fetchEquipo = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await miEquipoAPI.getEquipo();

            let equipoData = [];
            let usuarioData = null;
            let total = 0;

            if (Array.isArray(response.data)) {
                equipoData = response.data || [];
                // Usamos el usuario del contexto como fallback si no viene en la respuesta
                usuarioData = user;
                // Calculamos total aproximado
                total = Array.isArray(equipoData) ? equipoData.reduce((acc, area) => acc + (area._count?.cargos || 0), 0) : 0;
            } else if (response.data && response.data.equipo) {
                equipoData = response.data.equipo || [];
                usuarioData = response.data.usuario;
                total = response.data.totalPersonas;
            } else {
                console.warn('Respuesta con formato desconocido', response);
            }

            setData({
                equipo: equipoData,
                usuario: usuarioData,
                totalPersonas: total
            });
        } catch (err) {
            console.error('Error cargando equipo:', err.message);
            setError(err.response?.data?.message || 'Error al cargar la información del equipo');
            showError('No se pudo cargar la información de tu equipo');
        } finally {
            setLoading(false);
        }
    }, [showError, user]);

    useEffect(() => {
        fetchEquipo();
    }, [fetchEquipo]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Cargando jerarquía organizacional...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="flex flex-col items-center gap-4 p-8 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-200 dark:border-red-800 max-w-md">
                    <AlertCircle className="w-10 h-10 text-red-500" />
                    <p className="text-red-700 dark:text-red-300 text-center">{error}</p>
                    <button
                        onClick={fetchEquipo}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
                    >
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    const hasEquipo = data?.equipo && data.equipo.length > 0;

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                        <Network className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                            Mi Equipo
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Jerarquía organizacional y activos asignados
                        </p>
                    </div>
                </div>

                {/* Resumen del usuario */}
                {data?.usuario && (
                    <div className="mt-4 flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                            {data.usuario.nombre?.[0]}{data.usuario.apellido?.[0]}
                        </div>
                        <div className="flex-1">
                            <p className="font-semibold text-gray-800 dark:text-white">
                                {data.usuario.nombre} {data.usuario.apellido}
                            </p>
                            {data.usuario.cargos && data.usuario.cargos.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                    {data.usuario.cargos.map((c, i) => (
                                        <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                      ${c.esJefatura
                                                ? 'bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-300'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                                            }`}>
                                            <Briefcase className="w-3 h-3" />
                                            {c.cargo} · {c.area}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        {data.totalPersonas > 0 && (
                            <div className="text-right flex-shrink-0">
                                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{data.totalPersonas}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">persona{data.totalPersonas !== 1 ? 's' : ''}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Árbol jerárquico */}
            {hasEquipo && Array.isArray(data.equipo) ? (
                <div className="space-y-3">
                    {data.equipo.map((area, index) => (
                        <AreaNode
                            key={area.id || index}
                            area={area}
                            level={0}
                            onAssetAdded={fetchEquipo}
                            selectedPerson={selectedPerson}
                            setSelectedPerson={setSelectedPerson}
                            isAddModalOpen={isAddModalOpen}
                            setIsAddModalOpen={setIsAddModalOpen}
                            setSubmittingAsset={setSubmittingAsset}
                            submittingAsset={submittingAsset}
                        />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                        <Users className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Sin equipo asignado
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                        No tienes un equipo asignado en la jerarquía organizacional.
                        Contacta al administrador para ser incluido.
                    </p>
                </div>
            )}

            {/* Modal centralizado */}
            {isAddModalOpen && targetAddingUser && (
                <AddAssetModal
                    isOpen={isAddModalOpen}
                    onClose={() => setIsAddModalOpen(false)}
                    usuario={targetAddingUser}
                    isSubmitting={submittingAsset}
                    onAdd={async (formData) => {
                        try {
                            setSubmittingAsset(true);
                            await miEquipoAPI.asignarOperacional({
                                ...formData,
                                usuarioId: targetAddingUser.id
                            });
                            showSuccess('Activo operacional asignado con éxito');
                            setIsAddModalOpen(false);
                            // Limpiar selección para forzar refresh limpio
                            setSelectedPerson(null);
                            await fetchEquipo();
                        } catch (err) {
                            console.error(err);
                            const errorMsg = err.response?.data?.message || 'Error al asignar el activo operacional';
                            showError(errorMsg);
                        } finally {
                            setSubmittingAsset(false);
                        }
                    }}
                />
            )}
        </div>
    );
}
